/**
 * RouteLogic-sectie: routekaart (vandaag of gekozen dag) + omzetprognose vooruit.
 *
 * Vaste sectie, onafhankelijk van het periode-filter: de prognose gaat per
 * definitie over de toekomst. Data komt uit /api/schoorsteenveger/routes
 * (server-side gecachet; het dashboard praat nooit rechtstreeks met RouteLogic).
 *
 * Kaart-lessen uit de call-tracking-map hergebruikt: integer zoom (rastertiles
 * nooit herschalen), CartoDB-tiles, achtergrond in tile-kleur.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Wrench, Truck, Navigation, Route as RouteIcon } from 'lucide-react';
import InfoDot from '../shared/InfoDot';
import { EMPLOYEES } from '../../constants/schoorsteenvegerLogic';
import { cartoTileUrl } from '../../constants/basemap';
import { getLiveLocations, getRealisatie, getDrivenTracks } from '../../utils/schoorsteenvegerQueries';

// Stabiel kleurenpalet per chauffeur (volgorde van eerste verschijning)
const DRIVER_COLORS = ['#2563eb', '#D02E17', '#059669', '#d97706', '#7c3aed', '#0891b2'];

const eur0 = (v) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(v || 0);

const dayLabel = (ymd) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Amsterdam' });
};

// Datum voor de prognosetabel: weekdag + dd-mm als losse delen, zodat de
// datum kolom-strak uitlijnt (weekdag-afkortingen zijn proportioneel breed)
const dayParts = (ymd) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  const wd = d.toLocaleDateString('nl-NL', { weekday: 'short', timeZone: 'Europe/Amsterdam' });
  const [, m, day] = ymd.split('-');
  return { wd, dm: `${day}-${m}` };
};

const hhmm = (t) => (t ? String(t).substring(0, 5) : null);

// Minuten -> duur als UU:MM: 215 -> "03:35", 45 -> "00:45"
const fmtDur = (min) => {
  const m = Math.round(min || 0);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

// Bezettings-kleur: >100% overboekt (rood), 85-100 vol (oranje), anders groen
const utilColor = (u) => {
  if (u == null) return 'var(--color-neutral-400)';
  if (u > 100) return '#dc2626';
  if (u >= 85) return '#d97706';
  return '#059669';
};

// Tooltip die uitlegt wat het bezettingspercentage betekent
const utilTitle = (r) => {
  if (!r || r.utilization == null) return undefined;
  const status = r.utilization > 100 ? 'Overboekt: route langer dan de werkdag.'
    : r.utilization >= 85 ? 'Dag zit vol.'
    : 'Ruimte om bij te plannen.';
  const eind = r.plannedEnd ? `, thuis ${hhmm(r.plannedEnd)}` : '';
  return `Bezetting ${r.utilization}%: route van ${fmtDur(r.plannedMinutes)} `
    + `(${fmtDur(r.workMinutes)} klussen + ${fmtDur(r.travelMinutes)} rijden${eind}) `
    + `in een werkvenster van ${fmtDur(r.windowMinutes)} (${hhmm(r.startTime)}–${hhmm(r.endTime)}). ${status}`;
};

// Minuten sinds middernacht uit "HH:MM(:SS)" — voor het omhullende venster
const toMinOfDay = (t) => {
  const [h, m] = String(t || '0:0').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Eén monteur kan meerdere routes op een dag hebben (tweede bus, gesplitste
// route, of een leeg planning-restje in RouteLogic). De prognosetabel telt
// stops en omzet over álle routes op; de bezetting rekent alleen over de
// gevulde routes, tegen hun omhullende werkvenster — zo kan een leeg
// 12-uursvenster het overboekt-signaal niet verwateren. (Vroeger won
// stilzwijgend de laatste route de cel, waardoor een lege route een echte
// kon verbergen en gesplitste routes ondertelden.)
const aggregateDriverRoutes = (routes) => {
  const filled = routes.filter((r) => r.stops.length > 0);
  const agg = {
    routeCount: routes.length,
    stopsCount: routes.reduce((s, r) => s + r.stops.length, 0),
    value: Math.round(routes.reduce((s, r) => s + r.value, 0) * 100) / 100,
    utilization: null,
    utilTitleText: undefined,
    multiTitle: undefined,
  };
  if (filled.length === 1) {
    agg.utilization = filled[0].utilization;
    agg.utilTitleText = utilTitle(filled[0]);
  } else if (filled.length > 1) {
    const plannedMinutes = filled.reduce((s, r) => s + r.plannedMinutes, 0);
    const workMinutes = filled.reduce((s, r) => s + r.workMinutes, 0);
    const start = Math.min(...filled.map((r) => toMinOfDay(r.startTime)));
    const end = Math.max(...filled.map((r) => toMinOfDay(r.endTime)));
    const windowMinutes = Math.max(0, end - start);
    if (windowMinutes > 0 && plannedMinutes > 0) {
      agg.utilization = Math.round((plannedMinutes / windowMinutes) * 100);
      const status = agg.utilization > 100 ? 'Overboekt: routes samen langer dan de werkdag.'
        : agg.utilization >= 85 ? 'Dag zit vol.'
        : 'Ruimte om bij te plannen.';
      agg.utilTitleText = `Bezetting ${agg.utilization}%: ${filled.length} routes samen ${fmtDur(plannedMinutes)} `
        + `(waarvan ${fmtDur(workMinutes)} klussen) in een omhullend werkvenster van `
        + `${fmtDur(windowMinutes)} (${hhmm(filled.reduce((a, b) => (toMinOfDay(a.startTime) <= toMinOfDay(b.startTime) ? a : b)).startTime)}`
        + `–${hhmm(filled.reduce((a, b) => (toMinOfDay(a.endTime) >= toMinOfDay(b.endTime) ? a : b)).endTime)}). ${status}`;
    }
  }
  if (agg.routeCount > 1) {
    const parts = routes.map((r) => `${r.stops.length} stops ${eur0(r.value)} (${hhmm(r.startTime)}–${hhmm(r.endTime)}${r.licencePlate ? `, ${r.licencePlate}` : ''})`);
    agg.multiTitle = `${agg.routeCount} routes op deze dag: ${parts.join(' + ')}`;
  }
  return agg;
};

// Minimale HTML-escape voor tooltip-teksten uit TrackJack (namen/kentekens)
const esc = (s) => String(s == null ? '' : s).replace(/[<>&"]/g, (c) => (
  { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]
));

const RouteMap = ({ day, driverColor, isDark, live, tracks }) => {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markersRef = useRef([]);
  const liveMarkersRef = useRef([]);
  const trackLayerRef = useRef([]);
  const fitRef = useRef(null); // laatste fit-bounds, voor de centreer-knop

  useEffect(() => {
    if (!mapRef.current) return undefined;
    if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
    const map = L.map(mapRef.current, {
      center: [52.2, 5.5],
      zoom: 7,
      minZoom: 7,
      maxZoom: 13,
      maxBounds: [[50.5, 2.8], [53.8, 7.6]],
      maxBoundsViscosity: 1.0,
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: false,
    });
    L.tileLayer(cartoTileUrl(isDark), { maxZoom: 18 }).addTo(map);

    // Centreer-knop onder de zoom-knoppen: terug naar de standaard-fit van de
    // dag nadat er handmatig gezoomd/gesleept is.
    const Recenter = L.Control.extend({
      options: { position: 'topleft' },
      onAdd() {
        const div = L.DomUtil.create('div', 'leaflet-bar');
        const a = L.DomUtil.create('a', '', div);
        a.href = '#';
        a.title = 'Kaart centreren';
        a.setAttribute('aria-label', 'Kaart centreren');
        a.style.cssText = 'display:flex;align-items:center;justify-content:center;';
        a.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="12" r="7"/><line x1="12" y1="1.5" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22.5"/><line x1="1.5" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22.5" y2="12"/></svg>';
        L.DomEvent.on(a, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          L.DomEvent.stopPropagation(e);
          if (fitRef.current) map.fitBounds(fitRef.current, { padding: [24, 24], maxZoom: 11 });
        });
        return div;
      },
    });
    map.addControl(new Recenter());

    leafletRef.current = map;
    return () => { map.remove(); leafletRef.current = null; };
  }, [isDark]);

  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];
    if (!day) return;

    // Zoom fit alleen op de KLUSSEN van de dag; start/eind (thuisadres, vaak
    // Friesland) wordt wel getekend maar rekt de kaart niet meer op.
    const bounds = [];
    const homeBounds = [];
    day.routes.forEach((route) => {
      const color = driverColor(route.driver);

      // Start-/eindpunt (thuis, of hotel bij overnachting). Zelfde locatie ->
      // één gecombineerde marker.
      const sameSpot = route.startPoint && route.endPoint
        && route.startPoint.lat === route.endPoint.lat
        && route.startPoint.lng === route.endPoint.lng;
      const pointMarker = (pt, label, title, timeLabel) => {
        if (!pt || pt.lat == null) return;
        const w = label.length > 1 ? 30 : 24;
        const icon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            width: ${w}px; height: 24px; border-radius: 6px;
            background: ${color}; color: white;
            display: flex; align-items: center; justify-content: center;
            font-weight: 800; font-size: 10px; letter-spacing: 0;
            border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          ">${label}</div>`,
          iconSize: [w, 24],
          iconAnchor: [w / 2, 12],
        });
        const m = L.marker([pt.lat, pt.lng], { icon, zIndexOffset: 100 }).addTo(map);
        m.bindPopup(`
          <div style="font-size:13px; line-height:1.5; min-width: 160px;">
            <strong>${title} · ${route.driver}</strong><br/>
            ${pt.addressLine ? pt.addressLine + ', ' : ''}${pt.city || ''}<br/>
            ${timeLabel || ''}
          </div>
        `);
        markersRef.current.push(m);
        homeBounds.push([pt.lat, pt.lng]);
      };
      const vertrek = route.startPoint && route.startPoint.time ? `vertrek ${hhmm(route.startPoint.time)}` : null;
      const terug = route.endPoint && route.endPoint.time ? `terug ${hhmm(route.endPoint.time)}` : null;
      if (sameSpot) {
        pointMarker(route.startPoint, 'S/E', 'Start & eind', [vertrek, terug].filter(Boolean).join(' · '));
      } else {
        // Verschillend start- en eindpunt (bv. hotel bij overnachting):
        // twee losse markers op hun eigen locatie
        pointMarker(route.startPoint, 'S', 'Start', vertrek);
        pointMarker(route.endPoint, 'E', 'Einde', terug);
      }

      route.stops.forEach((stop) => {
        if (stop.lat == null || stop.lng == null) return;
        const icon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            width: 26px; height: 26px; border-radius: 50%;
            background: ${stop.delivered ? color : 'white'};
            border: 2.5px solid ${color};
            color: ${stop.delivered ? 'white' : color};
            display: flex; align-items: center; justify-content: center;
            font-weight: 700; font-size: 11px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.25);
          ">${stop.delivered ? '✓' : stop.orderIndex}</div>`,
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        });
        const marker = L.marker([stop.lat, stop.lng], { icon }).addTo(map);
        marker.bindPopup(`
          <div style="font-size:13px; line-height:1.5; min-width: 180px;">
            <strong>${stop.customer || stop.city || 'Stop ' + stop.orderIndex}</strong><br/>
            ${stop.city || ''} · ${route.driver}<br/>
            ${stop.service ? stop.service + '<br/>' : ''}
            ETA ${hhmm(stop.eta) || '—'} · ${stop.durationMinutes} min · <strong>${eur0(stop.price)}</strong><br/>
            ${stop.delivered ? '✅ afgerond' : '🕐 gepland'}
          </div>
        `);
        markersRef.current.push(marker);
        bounds.push([stop.lat, stop.lng]);
      });
    });
    const fitTo = bounds.length > 0 ? bounds : homeBounds;
    if (fitTo.length > 0) {
      fitRef.current = fitTo;
      map.fitBounds(fitTo, { padding: [24, 24], maxZoom: 11 });
    }
    // isDark in deps: de kaart wordt bij thema-wissel opnieuw aangemaakt,
    // dus de markers moeten dan ook opnieuw geplaatst worden
  }, [day, driverColor, isDark]);

  // Live posities van de bussen (TrackJack) — eigen laag die elke poll
  // ververst zonder de kaart of route-markers opnieuw op te bouwen.
  // Identiteit komt uit TrackJack zelf: de tracker-naam is de monteur
  // ("Remco"/"Niels"/"Fokke"), bewust onafhankelijk van aan wie RouteLogic
  // de bus die dag toewijst — een planning-restje mag de bus niet omkleuren.
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    liveMarkersRef.current.forEach((m) => map.removeLayer(m));
    liveMarkersRef.current = [];
    if (!live || !live.length) return;

    live.forEach((p) => {
      const color = driverColor(p.name);
      const ageMin = p.measuredOn ? Math.round((Date.now() - new Date(p.measuredOn).getTime()) / 60000) : null;
      const fresh = ageMin != null && ageMin <= 10;
      const arrow = p.direction != null
        ? `<div style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; transform: rotate(${p.direction}deg); font-size:9px; line-height:1; color:white;">▲</div>`
        : '';
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="position:relative; width:22px; height:22px; border-radius:50%; background:${color}; border:2.5px solid white; box-shadow:0 2px 8px rgba(0,0,0,0.35); opacity:${fresh ? 1 : 0.55};">${arrow}</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const naam = p.name || p.licencePlate || 'Onbekend';
      const tijd = p.measuredOn
        ? new Date(p.measuredOn).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })
        : '?';
      const marker = L.marker([p.lat, p.lng], { icon, zIndexOffset: 1000 })
        .bindTooltip(
          `<strong>${esc(naam)}</strong><br/>${esc(p.licencePlate || '')}${p.city ? ' · ' + esc(p.city) : ''}<br/>`
          + `${p.speed != null ? `${p.speed} km/u · ` : ''}${tijd}${fresh ? '' : ` (${ageMin} min geleden)`}`,
          { direction: 'top' }
        )
        .addTo(map);
      liveMarkersRef.current.push(marker);
    });
  }, [live, driverColor, isDark]);

  // Gereden spoor van vandaag (TrackJack) — dunne lijnen in monteurskleur,
  // alleen zichtbaar wanneer de toggle aan staat (tracks != null).
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    trackLayerRef.current.forEach((l) => map.removeLayer(l));
    trackLayerRef.current = [];
    if (!tracks || !tracks.length) return;
    tracks.forEach((t) => {
      const color = driverColor(t.naam);
      (t.lines || []).forEach((line) => {
        if (line.length < 2) return;
        const pl = L.polyline(line, { color, weight: 3, opacity: 0.55 }).addTo(map);
        trackLayerRef.current.push(pl);
      });
    });
  }, [tracks, driverColor, isDark]);

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height: '360px',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        // tile-kleur als achtergrond (subpixel-kieren onzichtbaar)
        background: isDark ? '#090a0f' : '#fafaf8',
        position: 'relative',
        isolation: 'isolate',
        zIndex: 0,
      }}
    />
  );
};

const RoutesSection = ({ data, loading, isDark = false }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  // TrackJack live posities: poll elke 45s (server cachet 30s). null zolang
  // de koppeling er niet is — de kaartlaag verbergt zichzelf dan.
  const [livePositions, setLivePositions] = useState(null);
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      const res = await getLiveLocations();
      if (!stopped) setLivePositions(res && res.connected ? res.positions : null);
    };
    tick();
    const iv = setInterval(tick, 45 * 1000);
    return () => { stopped = true; clearInterval(iv); };
  }, []);

  // TrackJack realisatie van vandaag (legenda-regel): 5 min verversen volstaat.
  const [todayStats, setTodayStats] = useState(null);
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      const res = await getRealisatie();
      if (!stopped) setTodayStats(res && res.connected ? res.todayStats : null);
    };
    tick();
    const iv = setInterval(tick, 5 * 60 * 1000);
    return () => { stopped = true; clearInterval(iv); };
  }, []);

  // Gereden spoor: lazy — pas opvragen wanneer de toggle aan gaat, daarna
  // elke 5 min verversen zolang hij aan staat (server cachet ook 5 min).
  const [showTrack, setShowTrack] = useState(false);
  const [tracks, setTracks] = useState(null);
  useEffect(() => {
    if (!showTrack) return undefined;
    let stopped = false;
    const tick = async () => {
      const res = await getDrivenTracks();
      if (!stopped) setTracks(res && res.connected ? res.tracks : []);
    };
    tick();
    const iv = setInterval(tick, 5 * 60 * 1000);
    return () => { stopped = true; clearInterval(iv); };
  }, [showTrack]);

  // Chauffeur -> kleur: eerst de vaste medewerker-kleur (match op voornaam of
  // volledige naam), anders een stabiele paletkleur voor onbekende chauffeurs.
  const driverColor = useMemo(() => {
    const emps = Object.values(EMPLOYEES);
    const palette = {};
    let i = 0;
    return (driver) => {
      if (!driver) return 'var(--color-neutral-500)';
      const d = String(driver).toLowerCase().trim();
      const emp = emps.find((e) => {
        const full = e.name.toLowerCase();
        return d === full || d === full.split(' ')[0];
      });
      if (emp) return emp.color;
      if (!(driver in palette)) { palette[driver] = DRIVER_COLORS[i % DRIVER_COLORS.length]; i += 1; }
      return palette[driver];
    };
  }, [data]);

  const days = useMemo(() => data?.days || [], [data]);

  // Vaste kolomvolgorde: monteurs op volgorde van eerste verschijning
  const drivers = useMemo(() => {
    const seen = [];
    days.forEach((d) => d.routes.forEach((r) => {
      if (!seen.includes(r.driver)) seen.push(r.driver);
    }));
    return seen;
  }, [days]);
  // Kaart toont: gekozen dag, anders vandaag, anders de eerstvolgende dag met routes
  const mapDay = useMemo(() => {
    if (!days.length) return null;
    if (selectedDate) return days.find((d) => d.date === selectedDate) || null;
    return days.find((d) => d.date === data.today) || days[0];
  }, [days, selectedDate, data]);

  if (loading && !data) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }} className="lg:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className="card-blue">
            <div className="card-body">
              <div style={{ height: '360px', background: 'var(--color-neutral-100)', borderRadius: 'var(--radius-md)' }} className="animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!data || days.length === 0) return null;

  const isToday = mapDay && mapDay.date === data.today;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }} className="lg:grid-cols-2">
      {/* Routekaart */}
      <div className="card-blue">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <h3 className="card-title">
            {isToday ? 'Routes vandaag' : `Routes ${mapDay ? dayLabel(mapDay.date) : ''}`}
          </h3>
          {isToday && (
            <button
              type="button"
              onClick={() => setShowTrack((v) => !v)}
              aria-pressed={showTrack}
              title={showTrack ? 'Gereden route verbergen' : 'Gereden route van vandaag tonen'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '4px 11px', borderRadius: '999px',
                border: `1px solid ${showTrack ? 'var(--color-blue-500)' : 'var(--color-neutral-300)'}`,
                background: showTrack ? 'var(--color-blue-500)' : 'transparent',
                color: showTrack ? '#fff' : 'var(--color-neutral-500)',
                fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                textTransform: 'none', letterSpacing: 0, whiteSpace: 'nowrap',
                transition: 'all 0.15s ease', flexShrink: 0,
              }}
            >
              <RouteIcon size={12} strokeWidth={2.4} aria-hidden />
              Route
            </button>
          )}
        </div>
        <div className="card-body">
          {mapDay ? (
            <>
              <RouteMap
                day={mapDay}
                driverColor={driverColor}
                isDark={isDark}
                live={isToday ? livePositions : null}
                tracks={isToday && showTrack ? tracks : null}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'var(--space-3)' }}>
                {(() => {
                  const seen = new Set();
                  return mapDay.routes.map((r) => {
                    // Realisatie-regel één keer per monteur, bij zijn eerste route-rij
                    const real = isToday && todayStats && !seen.has(r.driver) ? todayStats[r.driver] : null;
                    seen.add(r.driver);
                    return (
                      <div
                        key={r.driver + r.licencePlate}
                        title={r.stops.length === 0 ? 'Lege route in RouteLogic: monteur aan een bus gekoppeld zonder stops' : undefined}
                        style={{ opacity: r.stops.length === 0 ? 0.45 : 1 }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.82rem', flexWrap: 'wrap' }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: driverColor(r.driver), flexShrink: 0 }} />
                          <strong style={{ minWidth: '54px' }}>{r.driver}</strong>
                          <span style={{ color: 'var(--color-neutral-500)', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.deliveredCount}/{r.stops.length}</span>
                            {' · '}
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{hhmm(r.plannedStart) || hhmm(r.startTime)}–{hhmm(r.plannedEnd) || hhmm(r.endTime)}</span>
                            {' · '}
                            <Wrench size={12} strokeWidth={2.2} aria-hidden style={{ flexShrink: 0 }} />
                            <span style={{ fontVariantNumeric: 'tabular-nums' }} title={`Klustijd: ${fmtDur(r.workMinutes)}`}>{fmtDur(r.workMinutes)}</span>
                            <Truck size={12} strokeWidth={2.2} aria-hidden style={{ flexShrink: 0, marginLeft: '2px' }} />
                            <span style={{ fontVariantNumeric: 'tabular-nums' }} title={`Rijtijd: ${fmtDur(r.travelMinutes)}`}>{fmtDur(r.travelMinutes)}</span>
                          </span>
                          <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{eur0(r.value)}</span>
                          {r.utilization != null && (
                            <span
                              title={utilTitle(r)}
                              style={{ color: utilColor(r.utilization), fontWeight: 600, minWidth: '40px', textAlign: 'right', cursor: 'help' }}
                            >
                              {r.utilization}%
                            </span>
                          )}
                        </div>
                        {real && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.74rem', color: 'var(--color-neutral-500)', marginTop: '3px', paddingLeft: '20px' }}>
                            <Navigation size={11} strokeWidth={2.2} aria-hidden style={{ flexShrink: 0 }} />
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>vertrokken {real.vertrek} · onderweg tot {real.laatste} · {real.km} km</span>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--color-neutral-500)', fontSize: '0.875rem' }}>Geen routes gepland.</p>
          )}
        </div>
      </div>

      {/* Omzetprognose vooruit — tabel: datum | kolom per monteur | totaal */}
      <div className="card-blue">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">Omzetprognose planning</h3>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem', color: 'var(--color-neutral-500)', whiteSpace: 'nowrap' }}>
            {days.length} dagen
            <InfoDot text="Geplande omzet per dag per monteur uit RouteLogic (komende 90 dagen). Het percentage is de bezetting: routeduur (vertrek t/m thuiskomst) t.o.v. het werkvenster — groen = ruimte om bij te plannen, oranje = vol, rood = overboekt. Klik op een dag om die route op de kaart te tonen." />
          </span>
        </div>
        <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                <th style={{ textAlign: 'left', padding: '8px 12px 8px 16px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-200)' }}>
                  DATUM
                </th>
                {drivers.map((name) => (
                  <th key={name} style={{ textAlign: 'right', padding: '8px 12px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-600)', borderBottom: '1px solid var(--color-neutral-200)', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: driverColor(name), marginRight: 6, verticalAlign: 'baseline' }} />
                    {name.toUpperCase()}
                  </th>
                ))}
                <th style={{ textAlign: 'right', padding: '8px 16px 8px 12px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-200)' }}>
                  TOTAAL
                </th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => {
                const dayTotal = d.routes.reduce((s, r) => s + r.value, 0);
                const active = mapDay && mapDay.date === d.date;
                const isToday = d.date === data.today;
                const byDriver = {};
                d.routes.forEach((r) => { (byDriver[r.driver] = byDriver[r.driver] || []).push(r); });
                return (
                  <tr
                    key={d.date}
                    onClick={() => setSelectedDate(d.date === selectedDate ? null : d.date)}
                    style={{
                      cursor: 'pointer',
                      background: active
                        ? (isDark ? 'rgba(59, 130, 246, 0.18)' : 'var(--color-blue-50)')
                        : 'transparent',
                      borderBottom: '1px solid var(--color-neutral-100)',
                    }}
                  >
                    <td
                      title={isToday ? 'Vandaag' : undefined}
                      style={{ padding: '9px 12px 9px 16px', fontSize: '0.8rem', whiteSpace: 'nowrap', fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--color-blue-500)' : 'var(--color-neutral-800)' }}
                    >
                      <span style={{ display: 'inline-block', width: '2.1em' }}>{dayParts(d.date).wd}</span>
                      {dayParts(d.date).dm}
                    </td>
                    {drivers.map((name) => {
                      const rs = byDriver[name];
                      const a = rs ? aggregateDriverRoutes(rs) : null;
                      return (
                        <td key={name} style={{ textAlign: 'right', padding: '9px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {a ? (
                            <>
                              <span style={{ color: 'var(--color-neutral-400)', fontSize: '0.72rem', marginRight: 8 }}>{a.stopsCount}</span>
                              <span style={{ fontWeight: 600 }}>{eur0(a.value)}</span>
                              {a.routeCount > 1 && (
                                <span
                                  title={a.multiTitle}
                                  style={{ color: 'var(--color-neutral-400)', fontWeight: 700, fontSize: '0.68rem', marginLeft: 5, cursor: 'help' }}
                                >
                                  ×{a.routeCount}
                                </span>
                              )}
                              {a.utilization != null && (
                                <span
                                  title={a.utilTitleText}
                                  style={{ color: utilColor(a.utilization), fontWeight: 600, fontSize: '0.72rem', marginLeft: 6, display: 'inline-block', minWidth: '38px', textAlign: 'right', cursor: 'help' }}
                                >
                                  {a.utilization}%
                                </span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: 'var(--color-neutral-300)' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'right', padding: '9px 16px 9px 12px', fontSize: '0.8rem', fontWeight: 700 }}>
                      {eur0(dayTotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RoutesSection;
